import { useCallback, useEffect, useRef } from 'react';

export type BooleanCellEditorProps = {
  initialValue: unknown;
  /** When false, the NULL option is hidden. Defaults to true. */
  nullable?: boolean;
  onCommit: (value: unknown) => void;
  onCancel: () => void;
  inputClassName?: string;
  containerClassName?: string;
};

type BoolOption = 'true' | 'false' | 'null';

function formatLabel(opt: BoolOption): string {
  if (opt === 'true') return 'TRUE';
  if (opt === 'false') return 'FALSE';
  return 'NULL';
}

function optionValue(opt: BoolOption): boolean | null {
  if (opt === 'true') return true;
  if (opt === 'false') return false;
  return null;
}

function initialOption(value: unknown): BoolOption {
  if (value === true || value === 1 || value === '1' || value === 'true' || value === 'TRUE') {
    return 'true';
  }
  if (value === false || value === 0 || value === '0' || value === 'false' || value === 'FALSE') {
    return 'false';
  }
  return 'null';
}

/**
 * Inline segmented control for booleans. Three states when the column is
 * nullable, two states when it isn't. The current value is visually marked
 * and the editor commits as soon as the user picks an option (matches the
 * one-click commit pattern of EnumCellEditor / FkCellEditor).
 *
 * Esc cancels without committing.
 */
export function BooleanCellEditor({
  initialValue,
  nullable = true,
  onCommit,
  onCancel,
  containerClassName = 'relative w-full flex items-center',
}: BooleanCellEditorProps) {
  const initial = initialOption(initialValue);
  const containerRef = useRef<HTMLDivElement>(null);
  const options: BoolOption[] = nullable ? ['true', 'false', 'null'] : ['true', 'false'];

  // Focus the current value's button so keyboard Esc + Enter work without a
  // pointer step.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const idx = options.indexOf(initial);
      const target = idx >= 0 ? idx : 0;
      const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>('button[data-testid^="boolean-cell-editor-option-"]');
      buttons?.[target]?.focus();
    });
    return () => cancelAnimationFrame(id);
    // initial is derived from props at mount; options is stable for the lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = useCallback(
    (opt: BoolOption) => {
      const next = optionValue(opt);
      const same =
        (initial === 'true' && next === true) ||
        (initial === 'false' && next === false) ||
        (initial === 'null' && next === null);
      if (same) {
        onCancel();
        return;
      }
      onCommit(next);
    },
    [initial, onCancel, onCommit],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        if (!nullable) return;
        e.preventDefault();
        commit('null');
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [onCancel, nullable, commit],
  );

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      data-testid="boolean-cell-editor"
      onKeyDown={handleKeyDown}
      role="group"
      aria-label="Boolean value"
    >
      <div className="inline-flex items-stretch gap-0.5 border border-primary rounded-sm bg-background p-0.5">
        {options.map((opt) => {
          const isSelected = opt === initial;
          return (
            <button
              key={opt}
              type="button"
              className={`px-2 h-5 text-[10px] font-medium rounded-sm transition-colors ${
                isSelected
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-accent'
              }`}
              onClick={() => commit(opt)}
              data-testid={`boolean-cell-editor-option-${opt}`}
              data-selected={isSelected ? 'true' : 'false'}
              aria-pressed={isSelected}
            >
              {formatLabel(opt)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
